import { useEffect, useRef, useState } from "react";
import DailyIframe from "@daily-co/daily-js";
import { X, Loader2, AlertTriangle } from "lucide-react";

/**
 * Full-screen embedded Daily Prebuilt call.
 * Props:
 *  - roomUrl: Daily room URL (from backend)
 *  - token: meeting token (owner for teacher, participant for student)
 *  - subject / isHost: header labels
 *  - onClose: called when the user leaves / closes
 */
export const DailyRoomOverlay = ({ roomUrl, token, subject, isHost, onClose }) => {
  const containerRef = useRef(null);
  const frameRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!roomUrl || !token || !containerRef.current) return;
    let cancelled = false;
    let errored = false;
    const container = containerRef.current;

    (async () => {
      // Daily enforces a global singleton and destroy() is async — under React
      // StrictMode's double-mount we must AWAIT teardown of any existing
      // instance before creating a new one, else "Duplicate DailyIframe".
      const existing = DailyIframe.getCallInstance();
      if (existing) { try { await existing.destroy(); } catch { /* noop */ } }
      if (cancelled) return;
      const frame = DailyIframe.createFrame(container, {
        showLeaveButton: true,
        iframeStyle: { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" },
      });
      frameRef.current = frame;
      frame.on("error", (e) => {
        errored = true;
        const msg = e?.errorMsg || "";
        if (msg.includes("payment-method")) {
          setError("Your Daily.co video account needs a payment method added before classes can run. Open dashboard.daily.co → Billing and add a card (free tier still requires one — you won't be charged within the free limit).");
        } else {
          setError(msg || "The video call could not start.");
        }
      });
      // Only auto-close on a clean leave, not when a fatal error occurred.
      frame.on("left-meeting", () => { if (!errored) onClose && onClose(); });
      try { await frame.join({ url: roomUrl, token }); } catch (e) { console.error("Daily join failed", e); }
    })();

    return () => {
      cancelled = true;
      const f = DailyIframe.getCallInstance();
      if (f) { try { f.destroy(); } catch { /* noop */ } }
      frameRef.current = null;
    };
  }, [roomUrl, token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 flex flex-col" data-testid="daily-overlay">
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900 border-b border-slate-800 shrink-0">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white truncate" style={{ fontFamily: "Outfit" }}>{subject || "Online class"}</div>
          <div className="text-[11px] text-slate-400">{isHost ? "You are the host" : "Live class"} · in-app video</div>
        </div>
        <button onClick={onClose} className="inline-flex items-center gap-1.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold px-3.5 h-9 transition-colors shrink-0" data-testid="daily-leave-btn">
          <X className="h-4 w-4" /> Leave
        </button>
      </div>
      <div className="flex-1 relative">
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center p-6" data-testid="daily-error">
            <div className="max-w-md text-center space-y-3">
              <AlertTriangle className="h-10 w-10 mx-auto text-amber-400" />
              <div className="text-white font-semibold" style={{ fontFamily: "Outfit" }}>Video couldn't start</div>
              <p className="text-sm text-slate-300">{error}</p>
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-slate-500 pointer-events-none">
            <Loader2 className="h-6 w-6 animate-spin" /> <span className="ml-2 text-sm">Connecting…</span>
          </div>
        )}
        <div ref={containerRef} className="absolute inset-0" style={error ? { display: "none" } : undefined} />
      </div>
    </div>
  );
};

export default DailyRoomOverlay;
